import { create } from "zustand";
import { supabase } from "../lib/supabase";
import { Database } from "../lib/database";

type List = Database["public"]["Tables"]["lists"]["Row"];

export interface ListWithPlaceCount extends List {
  place_count: number;
}

export interface ListWithProfile {
  id: string;
  title: string;
  description: string | null;
  privacy_level: string | null;
  user_id: string;
  profiles: {
    username: string;
  };
}

interface ListState {
  lists: List[];
  listsWithPlaceCounts: ListWithPlaceCount[];
  currentList: List | null;
  listsLoading: boolean;
  error: string | null;

  // List management
  fetchLists: (userId: string) => Promise<void>;
  fetchListsWithPlaceCounts: (userId: string) => Promise<ListWithPlaceCount[]>;
  createList: (
    list: Database["public"]["Tables"]["lists"]["Insert"],
  ) => Promise<List>;
  updateList: (
    id: string,
    updates: Database["public"]["Tables"]["lists"]["Update"],
  ) => Promise<void>;
  deleteList: (id: string) => Promise<void>;
  setCurrentList: (list: List | null) => void;

  fetchList: (listId: string) => Promise<ListWithProfile | null>;

  // Utility functions
  generateItinerary: (listId: string) => Promise<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  clearError: () => void;
}

export const useListStore = create<ListState>((set) => ({
  lists: [],
  listsWithPlaceCounts: [],
  currentList: null,
  listsLoading: false,
  error: null,

  fetchLists: async (userId: string) => {
    try {
      set({ listsLoading: true, error: null });

      const { data, error } = await supabase
        .from("lists")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      set({ lists: data || [] });
    } catch (error) {
      const message = "Failed to fetch lists";
      console.error(message, error);
      set({ error: message });
    } finally {
      set({ listsLoading: false });
    }
  },

  fetchListsWithPlaceCounts: async (
    userId: string,
  ): Promise<ListWithPlaceCount[]> => {
    try {
      set({ listsLoading: true, error: null });

      // Query lists with place counts using a joined select
      const { data, error } = await supabase
        .from("lists")
        .select("*, list_places(count)")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      type ListWithPlaces = List & {
        list_places: { count: number }[];
      };
      // Transform the data to include place_count
      const listsWithCounts: ListWithPlaceCount[] = (data || []).map(
        (list: ListWithPlaces) => ({
          ...list,
          place_count: list.list_places?.[0]?.count || 0,
        }),
      );

      set({ listsWithPlaceCounts: listsWithCounts, lists: listsWithCounts });
      return listsWithCounts;
    } catch (error) {
      const message = "Failed to fetch lists with place counts";
      console.error(message, error);
      set({ error: message });
      return [];
    } finally {
      set({ listsLoading: false });
    }
  },

  createList: async (list) => {
    try {
      set({ listsLoading: true, error: null });

      const { data, error } = await supabase
        .from("lists")
        .insert(list)
        .select()
        .single();

      if (error) throw error;

      set((state) => ({ lists: [data, ...state.lists] }));
      return data;
    } catch (error) {
      const message = "Failed to create list";
      console.error(message, error);
      set({ error: message });
      throw error;
    } finally {
      set({ listsLoading: false });
    }
  },

  updateList: async (id, updates) => {
    try {
      set({ listsLoading: true, error: null });

      const { data, error } = await supabase
        .from("lists")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;

      set((state) => ({
        lists: state.lists.map((list) => (list.id === id ? data : list)),
        currentList: state.currentList?.id === id ? data : state.currentList,
      }));
    } catch (error) {
      const message = "Failed to update list";
      console.error(message, error);
      set({ error: message });
      throw error;
    } finally {
      set({ listsLoading: false });
    }
  },

  deleteList: async (id) => {
    try {
      set({ listsLoading: true, error: null });

      const { error } = await supabase.from("lists").delete().eq("id", id);

      if (error) throw error;

      set((state) => ({
        lists: state.lists.filter((list) => list.id !== id),
        currentList: state.currentList?.id === id ? null : state.currentList,
      }));
    } catch (error) {
      const message = "Failed to delete list";
      console.error(message, error);
      set({ error: message });
      throw error;
    } finally {
      set({ listsLoading: false });
    }
  },

  fetchList: async (listId: string): Promise<ListWithProfile | null> => {
    try {
      const { data, error } = await supabase
        .from("lists")
        .select(
          "id, title, description, privacy_level, user_id, profiles(username)",
        )
        .eq("id", listId)
        .maybeSingle();

      if (error) throw error;
      return data as ListWithProfile | null;
    } catch (error) {
      console.error("Failed to fetch list", error);
      return null;
    }
  },

  setCurrentList: (list) => {
    set({ currentList: list });
  },

  generateItinerary: async (listId) => {
    try {
      set({ listsLoading: true, error: null });

      // Fetch list data with places and photos
      const { data: list } = await supabase
        .from("lists")
        .select(
          `
          *,
          places(*),
          photos(*)
        `,
        )
        .eq("id", listId)
        .single();

      if (!list) throw new Error("List not found");

      // Generate itinerary based on places and photos
      const itinerary = {
        list: list,
        dailySchedule: [], // Process places and photos to create daily schedule
        summary: {
          totalDays: 0,
          placesVisited: list.places?.length || 0,
          photosCount: list.photos?.length || 0,
        },
      };

      return itinerary;
    } catch (error) {
      const message = "Failed to generate itinerary";
      console.error(message, error);
      set({ error: message });
      throw error;
    } finally {
      set({ listsLoading: false });
    }
  },

  clearError: () => set({ error: null }),
}));
